/**
 * Chargeback drill-down tree: Department -> Account -> Service -> Region -> Resource.
 *
 * Each node fetches its children only when expanded, and caches them. The whole
 * tree is never loaded: the bottom level is individual resources, and a real
 * department spans thousands of them, so building the tree up front would mean
 * waiting on every resource in the estate before the first row appeared. AWS
 * Cost Explorer makes the same trade - two grouping dimensions at a time, filter
 * to go deeper - and keeps resource-level detail in CUR behind an opt-in flag.
 *
 * "Unallocated" is a real row, not an error state. Spend with no Department tag
 * belongs to nobody, and that is exactly the number a finance team needs to see;
 * hiding it or spreading it across departments would make the statement balance
 * while making it a lie.
 */
import { useEffect, useState } from 'react';
import { ChevronRight, ChevronDown, Printer, AlertCircle, Loader2 } from 'lucide-react';

export interface DrilldownNode {
  key: string;
  label: string;
  cost: number;
  percentage: number;
  expandable: boolean;
  provider?: string;
}

type Level = 'department' | 'account' | 'service' | 'region' | 'resource';

const NEXT_LEVEL: Record<Level, Level | null> = {
  department: 'account',
  account: 'service',
  service: 'region',
  region: 'resource',
  resource: null,
};

const LEVEL_NOUN: Record<Level, string> = {
  department: 'department',
  account: 'account',
  service: 'service',
  region: 'region',
  resource: 'resource',
};

interface Props {
  apiBase: string;
  authHeaders: () => Record<string, string>;
  month: string;
  provider: string;
  onPrintDepartment: (department: string) => void;
}

const money = (v: number) => `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const providerBadge = (p?: string) => {
  if (!p) return null;
  const cls =
    p === 'aws'
      ? 'bg-amber-500/10 text-amber-600'
      : p === 'azure'
      ? 'bg-sky-500/10 text-sky-600'
      : 'bg-emerald-500/10 text-emerald-600';
  return <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${cls}`}>{p.toUpperCase()}</span>;
};

/** One row plus, when expanded, its lazily-fetched children. */
function TreeNode({
  node,
  level,
  path,
  depth,
  apiBase,
  authHeaders,
  month,
  provider,
  onPrintDepartment,
}: {
  node: DrilldownNode;
  level: Level;
  path: Record<string, string>;
  depth: number;
  apiBase: string;
  authHeaders: () => Record<string, string>;
  month: string;
  provider: string;
  onPrintDepartment: (d: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [children, setChildren] = useState<DrilldownNode[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);

  const childLevel = NEXT_LEVEL[level];

  const toggle = async () => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    if (children || !childLevel) return;
    setLoading(true);
    setErr(null);
    try {
      const qs = new URLSearchParams({ level: childLevel, month, ...path, [LEVEL_NOUN[level]]: node.key });
      if (provider && provider !== 'all') qs.set('provider', provider);
      const res = await fetch(`${apiBase}/tagging/chargeback/drilldown?${qs}`, { headers: authHeaders() });
      const body = await res.json();
      if (!res.ok) throw new Error(body.errors?.[0]?.message || `HTTP ${res.status}`);
      setChildren(body.data?.nodes || []);
      setTruncated(Boolean(body.data?.truncated));
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  const childPath = { ...path, [LEVEL_NOUN[level]]: node.key };

  return (
    <>
      <tr
        className={`border-t border-border ${node.expandable ? 'cursor-pointer hover:bg-muted/30' : ''}`}
        onClick={node.expandable ? toggle : undefined}
      >
        <td className="px-4 py-2" style={{ paddingLeft: `${depth * 22 + 16}px` }}>
          <div className="flex items-center gap-2">
            {node.expandable ? (
              open ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />
            ) : (
              <span className="inline-block w-4" />
            )}
            {providerBadge(node.provider)}
            <span className={`${depth === 0 ? 'font-medium' : ''} break-all`}>{node.label}</span>
          </div>
        </td>
        <td className="px-4 py-2 text-right tabular-nums whitespace-nowrap">{money(node.cost)}</td>
        <td className="px-4 py-2 text-right tabular-nums text-muted-foreground whitespace-nowrap">
          {node.percentage}%
        </td>
        <td className="px-4 py-2 text-right whitespace-nowrap">
          {level === 'department' && (
            <button
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                onPrintDepartment(node.key);
              }}
              title={`Print statement for ${node.label}`}
            >
              <Printer className="h-3 w-3" /> Print
            </button>
          )}
        </td>
      </tr>

      {open && loading && (
        <tr className="border-t border-border bg-muted/10">
          <td colSpan={4} className="px-4 py-2" style={{ paddingLeft: `${(depth + 1) * 22 + 16}px` }}>
            <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading {childLevel}s…
            </span>
          </td>
        </tr>
      )}

      {open && err && (
        <tr className="border-t border-border bg-rose-500/5">
          <td colSpan={4} className="px-4 py-2 text-xs text-rose-600" style={{ paddingLeft: `${(depth + 1) * 22 + 16}px` }}>
            <span className="inline-flex items-center gap-1">
              <AlertCircle className="h-3 w-3" /> {err}
            </span>
          </td>
        </tr>
      )}

      {open && children && children.length === 0 && !loading && (
        <tr className="border-t border-border bg-muted/10">
          <td colSpan={4} className="px-4 py-2 text-xs text-muted-foreground" style={{ paddingLeft: `${(depth + 1) * 22 + 16}px` }}>
            No {childLevel} breakdown available for this {LEVEL_NOUN[level]}.
          </td>
        </tr>
      )}

      {open &&
        children &&
        childLevel &&
        children.map((c) => (
          <TreeNode
            key={`${childLevel}:${c.key}`}
            node={c}
            level={childLevel}
            path={childPath}
            depth={depth + 1}
            apiBase={apiBase}
            authHeaders={authHeaders}
            month={month}
            provider={provider}
            onPrintDepartment={onPrintDepartment}
          />
        ))}

      {/* Reconciliation guard. Children must add up to their parent; when they
          do not, the level below did not honour one of the path filters and the
          numbers on screen are for a wider scope than the row they sit under.
          Observed on a tenant whose mock cost path ignores tag filters: every
          department expanded to the WHOLE tenant's accounts, which looks
          completely plausible and is completely wrong. Better to say so than to
          let someone chargeback off it. */}
      {open && children && children.length > 0 && !loading && (() => {
        const sum = children.reduce((s, c) => s + c.cost, 0);
        const drift = Math.abs(sum - node.cost);
        if (node.cost <= 0 || drift <= Math.max(1, node.cost * 0.01)) return null;
        return (
          <tr className="border-t border-border bg-amber-500/5">
            <td colSpan={4} className="px-4 py-2 text-xs text-amber-700 dark:text-amber-400" style={{ paddingLeft: `${(depth + 1) * 22 + 16}px` }}>
              <span className="inline-flex items-center gap-1">
                <AlertCircle className="h-3 w-3 shrink-0" />
                These {money(sum)} do not add up to {money(node.cost)} — this tenant's cost data
                does not support filtering by {LEVEL_NOUN[level]}, so the breakdown below is for a
                wider scope. Do not chargeback from it.
              </span>
            </td>
          </tr>
        );
      })()}

      {open && truncated && (
        <tr className="border-t border-border bg-amber-500/5">
          <td colSpan={4} className="px-4 py-2 text-xs text-amber-700 dark:text-amber-400" style={{ paddingLeft: `${(depth + 1) * 22 + 16}px` }}>
            Showing the highest-cost resources only — this {LEVEL_NOUN[level]} has more than the display limit.
          </td>
        </tr>
      )}
    </>
  );
}

export function ChargebackDrilldown({ apiBase, authHeaders, month, provider, onPrintDepartment }: Props) {
  const [roots, setRoots] = useState<DrilldownNode[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const qs = new URLSearchParams({ level: 'department', month });
        if (provider && provider !== 'all') qs.set('provider', provider);
        const res = await fetch(`${apiBase}/tagging/chargeback/drilldown?${qs}`, { headers: authHeaders() });
        const body = await res.json();
        if (!res.ok) throw new Error(body.errors?.[0]?.message || `HTTP ${res.status}`);
        if (cancelled) return;
        setRoots(body.data?.nodes || []);
        setTotal(body.data?.total || 0);
      } catch (e: any) {
        if (!cancelled) setErr(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, provider]);

  if (loading)
    return (
      <div className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
        Building chargeback breakdown…
      </div>
    );
  if (err)
    return (
      <div className="rounded-lg border border-border bg-card p-6 text-sm text-rose-600">
        <span className="inline-flex items-center gap-2">
          <AlertCircle className="h-4 w-4" /> {err}
        </span>
      </div>
    );

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
        <span className="text-sm font-medium">Cost breakdown</span>
        <span className="text-xs text-muted-foreground">
          Click any row to expand · Department → Account → Service → Region → Resource
        </span>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-4 py-2.5 text-left font-medium">Department</th>
            <th className="px-4 py-2.5 text-right font-medium">Cost</th>
            <th className="px-4 py-2.5 text-right font-medium">% of total</th>
            <th className="px-4 py-2.5 text-right font-medium">Statement</th>
          </tr>
        </thead>
        <tbody>
          {roots.map((n) => (
            <TreeNode
              key={`department:${n.key}`}
              node={n}
              level="department"
              path={{}}
              depth={0}
              apiBase={apiBase}
              authHeaders={authHeaders}
              month={month}
              provider={provider}
              onPrintDepartment={onPrintDepartment}
            />
          ))}
          {roots.length === 0 && (
            <tr>
              <td colSpan={4} className="px-4 py-6 text-center text-sm text-muted-foreground">
                No chargeback data for this period.
              </td>
            </tr>
          )}
          <tr className="border-t-2 border-border bg-muted/30 font-medium">
            <td className="px-4 py-2.5">Total</td>
            <td className="px-4 py-2.5 text-right tabular-nums">{money(total)}</td>
            <td className="px-4 py-2.5 text-right text-muted-foreground">100%</td>
            <td />
          </tr>
        </tbody>
      </table>
    </div>
  );
}
